import { useCallback, useEffect, useState } from "react";
import { Mail, Phone, Pencil, Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError, isAdmin, loadRoles, roleLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const roleBadge = {
  company_admin: "bg-violet-50 text-violet-700 border-violet-200",
  manager: "bg-blue-50 text-blue-700 border-blue-200",
  supervisor: "bg-teal-50 text-teal-700 border-teal-200",
  employee: "bg-slate-100 text-slate-700 border-slate-200",
};

const L = ({ children }) => (
  <Label className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{children}</Label>
);

export default function Staff() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const admin = isAdmin(user?.role) && !company?.demo_readonly;
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState("");
  const [newRoleBase, setNewRoleBase] = useState("employee");
  const [roleBusy, setRoleBusy] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);

  const loadRoleList = useCallback(async () => {
    try {
      setRoles(await loadRoles());
    } catch (e) {
      console.error("Kunde inte hämta roller", e);
    }
  }, []);

  const createRole = async () => {
    const label = newRole.trim();
    if (!label) return;
    setRoleBusy(true);
    try {
      const { data } = await api.post("/roles", { label, base: newRoleBase });
      await loadRoleList();
      setForm((f) => ({ ...f, role: data.slug }));
      setNewRole("");
      setRoleOpen(false);
      toast.success(t("staff.roleCreated", { name: data.label }));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRoleBusy(false);
    }
  };

  const removeRole = async (slug, label) => {
    if (!window.confirm(t("staff.roleDeleteConfirm", { name: label }))) return;
    try {
      await api.delete(`/roles/${slug}`);
      await loadRoleList();
      if (form.role === slug) setForm((f) => ({ ...f, role: "employee" }));
      toast.success(t("staff.roleRemoved"));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setStaff(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    loadRoleList();
  }, [load, loadRoleList]);

  const openDialog = (member) => {
    setForm({
      id: member?.id,
      name: member?.name || "",
      email: member?.email || "",
      role: member?.role || "employee",
      phone: member?.phone || "",
      title: member?.title || "",
      password: "",
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        phone: form.phone,
        title: form.title,
      };
      if (form.password) payload.password = form.password;
      if (form.id) await api.put(`/users/${form.id}`, payload);
      else await api.post("/users", payload);
      toast.success(form.id ? t("staff.saved") : t("staff.added"));
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(t("staff.deleteConfirm", { name: m.name }))) return;
    try {
      await api.delete(`/users/${m.id}`);
      toast.success(t("staff.removed"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const filtered = filter === "all" ? staff : staff.filter((s) => s.role === filter);

  return (
    <div data-testid="staff-page">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{t("staff.title")}</h1>
          <p className="text-sm text-slate-500">{t("staff.subtitle")}</p>
        </div>
        {admin && (
          <Button
            data-testid="staff-new-button"
            onClick={() => openDialog(null)}
            className="ml-auto h-10 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
          >
            <Plus className="mr-1.5 h-4 w-4" /> {t("common.add")}
          </Button>
        )}
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList className="h-auto flex-wrap bg-slate-100">
          <TabsTrigger value="all" data-testid="staff-tab-all" className="text-sm">
            {t("common.all")} ({staff.length})
          </TabsTrigger>
          {roles.map((r) => (
            <TabsTrigger key={r.slug} value={r.slug} data-testid={`staff-tab-${r.slug}`} className="text-sm">
              {roleLabel(r.slug, t)} ({staff.filter((s) => s.role === r.slug).length})
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((m) => (
          <div
            key={m.id}
            data-testid={`staff-card-${m.id}`}
            className="rounded-lg border border-slate-200 bg-white p-4 transition-colors duration-200 hover:border-slate-300"
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-900 text-sm font-semibold text-white">
                {m.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-base font-semibold text-slate-900">{m.name}</h3>
                <span
                  className={`mt-1 inline-block rounded border px-1.5 py-0.5 text-[11px] font-medium ${
                    roleBadge[m.role] || "border-amber-200 bg-amber-50 text-amber-800"
                  }`}
                  data-testid={`staff-role-${m.id}`}
                >
                  {roleLabel(m.role, t)}
                </span>
                {m.title && <p className="mt-1 text-xs text-slate-500">{m.title}</p>}
              </div>
            </div>

            <div className="mt-3 space-y-1 text-sm">
              <a
                href={`mailto:${m.email}`}
                className="flex items-center gap-2 text-slate-600 transition-colors duration-200 hover:text-blue-600"
              >
                <Mail className="h-3.5 w-3.5" /> <span className="truncate">{m.email}</span>
              </a>
              {m.phone && (
                <a
                  href={`tel:${m.phone}`}
                  data-testid={`staff-phone-${m.id}`}
                  className="flex items-center gap-2 text-slate-600 transition-colors duration-200 hover:text-blue-600"
                >
                  <Phone className="h-3.5 w-3.5" /> {m.phone}
                </a>
              )}
            </div>

            {admin && (
              <div className="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`staff-edit-${m.id}`}
                  onClick={() => openDialog(m)}
                  className="h-9 flex-1 bg-white transition-colors duration-200 hover:bg-slate-100"
                >
                  <Pencil className="mr-1.5 h-3.5 w-3.5" /> {t("staff.editBtn")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`staff-delete-${m.id}`}
                  onClick={() => remove(m)}
                  className="h-9 border-red-200 bg-white text-red-600 transition-colors duration-200 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      {filtered.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="staff-empty">
          {t("staff.empty")}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="staff-dialog" className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {form.id ? t("staff.editTitle") : t("staff.newTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <L>{t("staff.name")}</L>
              <Input
                data-testid="staff-name-input"
                value={form.name || ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 h-10"
              />
            </div>
            <div>
              <L>{t("staff.email")}</L>
              <Input
                data-testid="staff-email-input"
                type="email"
                value={form.email || ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 h-10"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <L>{t("staff.role")}</L>
                <Select open={roleOpen} onOpenChange={setRoleOpen} value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-role-select" className="mt-1 h-10">
                    <SelectValue>{roleLabel(form.role, t)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {/* Skapa egen roll */}
                    <div className="border-b border-slate-200 p-2" data-testid="staff-custom-role-box">
                      <div className="flex gap-1.5">
                        <Input
                          data-testid="staff-custom-role-input"
                          value={newRole}
                          placeholder={t("staff.customRolePlaceholder")}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter") {
                              e.preventDefault();
                              createRole();
                            }
                          }}
                          onChange={(e) => setNewRole(e.target.value)}
                          className="h-9"
                        />
                        <Button
                          data-testid="staff-custom-role-add"
                          onClick={createRole}
                          disabled={roleBusy || !newRole.trim()}
                          className="h-9 shrink-0 bg-blue-600 px-2.5 text-white transition-colors duration-200 hover:bg-blue-700"
                        >
                          {roleBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        </Button>
                      </div>
                      <div className="mt-1.5 flex gap-1.5">
                        {[["employee", t("staff.baseEmployee")], ["supervisor", t("staff.baseLeader")]].map(([v, label]) => (
                          <button
                            key={v}
                            type="button"
                            data-testid={`staff-custom-role-base-${v}`}
                            onClick={() => setNewRoleBase(v)}
                            className={`rounded-md border px-2 py-1 text-xs font-medium transition-colors duration-200 ${
                              newRoleBase === v
                                ? "border-blue-500 bg-blue-50 text-blue-700"
                                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {roles.map((r) => (
                      <div key={r.slug} className="flex items-center">
                        <SelectItem value={r.slug} data-testid={`staff-role-option-${r.slug}`} className="flex-1">
                          {roleLabel(r.slug, t)}
                          {!r.builtin && (
                            <span className="ml-1.5 text-xs text-slate-400">
                              {r.base === "supervisor" ? t("staff.baseLeader") : t("staff.baseEmployee")}
                            </span>
                          )}
                        </SelectItem>
                        {!r.builtin && (
                          <button
                            type="button"
                            data-testid={`staff-role-delete-${r.slug}`}
                            onClick={(e) => { e.stopPropagation(); removeRole(r.slug, r.label); }}
                            className="mr-2 rounded p-1.5 text-red-600 transition-colors duration-200 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <L>{t("staff.phone")}</L>
                <Input
                  data-testid="staff-phone-input"
                  value={form.phone || ""}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1 h-10"
                />
              </div>
            </div>
            <div>
              <L>{t("staff.jobTitle")}</L>
              <Input
                data-testid="staff-title-input"
                value={form.title || ""}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 h-10"
              />
            </div>
            <div>
              <L>{form.id ? "Nytt lösenord (valfritt)" : "Lösenord"}</L>
              <Input
                data-testid="staff-password-input"
                type="password"
                value={form.password || ""}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 h-10"
                placeholder={form.id ? t("staff.leaveEmpty") : "Faltmastare123!"}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="staff-save-button"
              onClick={save}
              disabled={busy}
              className="bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
            >
              {t("common.save")}
            </Button>          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
